import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { getApiHost, apiFetch } from '../utils/api';

const API_BASE_URL = getApiHost();

type Status = 'idle' | 'confirming' | 'success' | 'error';

const ConnectTelegramPage: React.FC = () => {
  const { user, isAuthenticated, isLoading, requestLogin } = useAuth();
  const [searchParams] = useSearchParams();
  const code = (searchParams.get('code') || '').trim();

  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [linkedUsername, setLinkedUsername] = useState<string | null>(null);

  const confirmLink = useCallback(async () => {
    const token = localStorage.getItem('deepnets_token');
    if (!token) return;
    setStatus('confirming');
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/telegram/link/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus('error');
        setError(data?.error || 'Could not link your Telegram account.');
        return;
      }
      setLinkedUsername(data?.telegramUsername ?? null);
      setStatus('success');
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  }, [code]);

  // Auto-confirm once we have both a code and an authenticated session.
  useEffect(() => {
    if (code && isAuthenticated && status === 'idle') {
      confirmLink();
    }
  }, [code, isAuthenticated, status, confirmLink]);

  const renderBody = () => {
    if (!code) {
      return (
        <p className="ct-msg ct-error">
          Missing link code. Open Telegram, send <code>/link</code> to{' '}
          <strong>@deepnetsbot</strong>, and tap the link it gives you.
        </p>
      );
    }
    if (isLoading) {
      return <p className="ct-msg">Loading…</p>;
    }
    if (!isAuthenticated) {
      return (
        <>
          <p className="ct-msg">
            Sign in with the wallet on your deepnets account to finish linking Telegram.
          </p>
          <button
            className="ct-btn"
            onClick={() => requestLogin(`/connect/telegram?code=${encodeURIComponent(code)}`)}
          >
            Sign in
          </button>
        </>
      );
    }
    if (status === 'confirming' || status === 'idle') {
      return <p className="ct-msg">Linking your Telegram account…</p>;
    }
    if (status === 'success') {
      return (
        <>
          <p className="ct-msg ct-success">
            ✓ Telegram linked{linkedUsername ? ` as @${linkedUsername}` : ''}.
          </p>
          <p className="ct-msg">
            Head back to <strong>@deepnetsbot</strong> and send{' '}
            <code>/safety &lt;mint or $ticker&gt;</code> to get a full report.
          </p>
          <Link className="ct-btn" to="/">Done</Link>
        </>
      );
    }
    return (
      <>
        <p className="ct-msg ct-error">{error}</p>
        <button className="ct-btn" onClick={confirmLink}>Try again</button>
      </>
    );
  };

  return (
    <div className="app-container">
      <Header />
      <div className="ct-wrap">
        <div className="ct-card">
          <h1 className="ct-title">Connect Telegram</h1>
          {isAuthenticated && user?.walletAddress && (
            <p className="ct-sub">
              Signed in as {user.walletAddress.slice(0, 4)}…{user.walletAddress.slice(-4)}
            </p>
          )}
          {renderBody()}
        </div>
      </div>
      <Footer />
      <style>{`
        .ct-wrap { display: flex; justify-content: center; padding: 48px 16px; }
        .ct-card { max-width: 480px; width: 100%; background: var(--card-bg, #14161c);
          border: 1px solid var(--border, #262a33); border-radius: 12px; padding: 28px; }
        .ct-title { margin: 0 0 4px; font-size: 22px; }
        .ct-sub { margin: 0 0 16px; opacity: 0.7; font-size: 13px; }
        .ct-msg { margin: 12px 0; line-height: 1.5; }
        .ct-success { color: #36d399; }
        .ct-error { color: #f87272; }
        .ct-btn { display: inline-block; margin-top: 12px; padding: 10px 18px;
          background: #2f6fed; color: #fff; border: none; border-radius: 8px;
          font-size: 14px; cursor: pointer; text-decoration: none; }
        .ct-btn:hover { background: #2a63d4; }
      `}</style>
    </div>
  );
};

export default ConnectTelegramPage;
